export interface CurrencyTotal {
  currency: string;
  amount: number;
  budgets: number;
}

/** Mirrors `EngagementTier` on the backend. */
export type EngagementTier = 'never_started' | 'abandoned' | 'dormant' | 'active' | 'power';

export interface UserEngagement {
  userId: string;
  email: string | null;
  name: string | null;
  organization: string | null;
  createdAt: string | null;
  subscriptionStatus: string | null;
  planName: string | null;
  /** The plan's list price. Zero for complimentary and trial plans. */
  planPrice: number | null;
  budgets: number;
  emptyBudgets: number;
  categories: number;
  accounts: number;
  lines: number;
  /** What their budgets add up to, in `currency`. */
  budgetValue: number;
  currency: string | null;
  /** How many of their budgets are in `currency` — not their whole budget count. */
  budgetsInCurrency: number;
  /** Their full breakdown, largest first; `budgetValue` is its head. */
  valueByCurrency: CurrencyTotal[];
  /** True when they also hold budgets in another currency, excluded from the total. */
  hasMixedCurrencies: boolean;
  edits: number;
  sharedWithThem: number;
  lastActivityAt: string | null;
  daysSinceActivity: number | null;
  tier: EngagementTier;
}

export interface EngagementOverview {
  users: number;
  activated: number;
  engaged: number;
  activeRecently: number;
  byTier: Record<EngagementTier, number>;
  budgets: number;
  emptyBudgets: number;
  lines: number;
  /** Budgeted value per currency, largest first. Never summed across them. */
  valueByCurrency: CurrencyTotal[];
  /** Live subscription on a plan that actually charges. */
  paying: number;
  /** Paying, but never got past creating a budget. */
  payingNotUsing: number;
  /** Paying and actively using it — the real customer count. */
  payingAndUsing: number;
  /** Actively using it on no paid plan. */
  usingNotPaying: number;
  /** Did real work, then cancelled or lapsed. */
  lostAfterRealWork: number;
  activationRate: number;
  engagementRate: number;
  abandonmentRate: number;
  medianLinesPerEngagedUser: number;
}

export interface EngagementReport {
  overview: EngagementOverview;
  users: UserEngagement[];
}

export interface EngagementFilters {
  search: string;
  tiers: EngagementTier[];
  statuses: string[];
}

/**
 * Narrows the rows in the browser.
 *
 * The report is a single expensive aggregate over the whole project, and the
 * overview it carries describes the entire user base by definition — so
 * filtering here rather than re-querying makes every filter instant and keeps
 * the funnel steady while the table moves.
 */
export function applyFilters(
  users: UserEngagement[],
  { search, tiers, statuses }: EngagementFilters,
): UserEngagement[] {
  const term = search.trim().toLowerCase();
  const wantedTiers = new Set(tiers);
  const wantedStatuses = new Set(statuses.map((status) => status.toLowerCase()));

  return users.filter((user) => {
    if (wantedTiers.size > 0 && !wantedTiers.has(user.tier)) {
      return false;
    }

    if (
      wantedStatuses.size > 0 &&
      !wantedStatuses.has((user.subscriptionStatus ?? NO_SUBSCRIPTION).toLowerCase())
    ) {
      return false;
    }

    if (!term) {
      return true;
    }

    return [user.email, user.name, user.organization].some((field) =>
      field?.toLowerCase().includes(term),
    );
  });
}

/** Filter value for users who never had a subscription. */
export const NO_SUBSCRIPTION = 'none';

/**
 * The ladder, in order, with the words a person would actually use. The
 * descriptions are what make the page self-explanatory — the tier names alone
 * do not say where the bar is.
 */
export const TIERS: Array<{
  value: EngagementTier;
  label: string;
  description: string;
  tone: 'positive' | 'warning' | 'neutral';
}> = [
  {
    value: 'never_started',
    label: 'Never started',
    description: 'never created a budget',
    tone: 'neutral',
  },
  {
    value: 'abandoned',
    label: 'Created & left',
    description: 'made a budget, left it empty',
    tone: 'warning',
  },
  {
    value: 'dormant',
    label: 'Gone quiet',
    description: 'real work, but silent 90+ days',
    tone: 'warning',
  },
  {
    value: 'active',
    label: 'Active',
    description: 'building, and back recently',
    tone: 'positive',
  },
  {
    value: 'power',
    label: 'Power user',
    description: 'several budgets, thousands of lines',
    tone: 'positive',
  },
];

const TIER_BY_VALUE = new Map(TIERS.map((tier) => [tier.value, tier]));

export function tierLabel(tier: EngagementTier): string {
  return TIER_BY_VALUE.get(tier)?.label ?? tier;
}

export function tierTone(tier: EngagementTier): 'positive' | 'warning' | 'neutral' {
  return TIER_BY_VALUE.get(tier)?.tone ?? 'neutral';
}

/** `1,234` — thousands separated, because these counts run to five figures. */
export function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

/** `29%` — whole percentages; a tenth of a point means nothing at this scale. */
export function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

/** "13 days ago", "Today", "Never". */
export function describeRecency(days: number | null): string {
  if (days === null) {
    return 'Never';
  }

  if (days === 0) {
    return 'Today';
  }

  if (days === 1) {
    return 'Yesterday';
  }

  return `${days} days ago`;
}

/**
 * `$18.8M`, `฿328.6M`, `AED 61.6M` — compact, because budget values run to nine
 * figures and the exact cent is never the point at this altitude.
 *
 * Falls back to the bare code when the currency is not one `Intl` recognises:
 * a handful of budgets carry a truncated code such as "US".
 */
export function formatMoney(amount: number, currency: string | null): string {
  const compact = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);

  if (!currency) {
    return compact;
  }

  try {
    // Intl throws on a malformed code rather than degrading, so this is the
    // check as well as the formatter.
    const symbol = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount);

    return symbol;
  } catch {
    return `${compact} ${currency}`;
  }
}

/** Statuses that mean a subscription is in force. Mirrors the backend's set. */
const LIVE_STATUSES = new Set(['active', 'trial', 'trialing', 'past_due']);

/**
 * Whether this user is actually generating revenue.
 *
 * A live subscription is not the same as a paying customer: most live
 * subscriptions here sit on complimentary plans priced at zero.
 */
export function isPaying(user: UserEngagement): boolean {
  const status = user.subscriptionStatus?.trim().toLowerCase();

  return Boolean(status && LIVE_STATUSES.has(status)) && (user.planPrice ?? 0) > 0;
}

/** The three states that matter when asking whether someone uses the product. */
export type UsageState = 'using' | 'quiet' | 'never';

export function usageStateOf(user: UserEngagement): UsageState {
  if (user.tier === 'active' || user.tier === 'power') {
    return 'using';
  }

  return user.tier === 'dormant' ? 'quiet' : 'never';
}
